import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getTrainingSettings, updateTrainingSettings } from './api';

// ZU's fixed A/V script text: the slides every class carries word for word.
//
// These do not go through the model. A paraphrased copyright notice or an invented
// narrator name is a governance problem, not a quality one, so the narration is stored
// here and interpolated with the class's own details.
const FIELDS = [
  { key: 'av_narrator_name', label: 'Narrator name',
    hint: 'Named in the welcome narration. Ships blank on purpose.', rows: 1 },
  { key: 'av_welcome_vo', label: 'Welcome slide narration',
    hint: 'Opens every class.', rows: 4 },
  { key: 'av_copyright_vo', label: 'Copyright notice narration', rows: 2 },
  { key: 'av_resources_vo', label: 'Guidance and resources narration', rows: 4 },
  { key: 'av_knowledge_check_vo', label: 'Knowledge check transition narration',
    hint: 'Spoken before a check. This counts toward the narration budget.', rows: 2 },
  { key: 'av_part_conclusion_vo', label: 'Part break narration', rows: 2 },
  { key: 'av_final_quiz_vo', label: 'Final quiz instruction narration', rows: 4 },
  { key: 'av_production_notes', label: 'Production notes',
    hint: 'Appended to the end of every script for the production team.', rows: 4 },
  { key: 'av_pronunciation_seed', label: 'Pronunciation key',
    hint: 'One per line, TERM: how it is said. JPRO adds the terms a class actually uses.',
    rows: 6 },
];

const PLACEHOLDERS = ['{class_name}', '{pathway_name}', '{minutes}', '{parts}', '{part_minutes}',
                      '{narrator_name}', '{passing_score}'];

export default function AvBoilerplateSettings() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['training-settings'], queryFn: getTrainingSettings, retry: false,
  });
  const [form, setForm] = useState({});
  // Seed once: the app refetches on focus, and re-seeding would wipe text being typed
  // after an alt-tab.
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || !data?.settings) return;
    seeded.current = true;
    const next = {};
    FIELDS.forEach(({ key }) => { next[key] = data.settings[key] || ''; });
    setForm(next);
  }, [data]);

  const save = useMutation({
    mutationFn: () => updateTrainingSettings(form),
    onSuccess: () => {
      toast.success('A/V script text saved');
      qc.invalidateQueries({ queryKey: ['training-settings'] });
    },
    onError: (e) => toast.error(e?.message || 'Could not save'),
  });

  if (isLoading) return <div className="text-xs text-gray-500">Loading...</div>;
  if (isError) return <div className="text-xs text-red-500">Could not load the A/V script text.</div>;

  const defaults = data?.av_boilerplate_defaults || {};
  const field = 'w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 mt-1 font-mono';

  return (
    <div className="max-w-[720px]">
      <h3 className="text-sm font-semibold mb-1">A/V script text</h3>
      <p className="text-xs text-gray-500 mb-2">
        The slides every A/V script carries word for word. JPRO does not write these and
        does not rephrase them: it fills in the class details and uses the text exactly as
        you leave it. Blank means use the shipped wording.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        You can use {PLACEHOLDERS.join(', ')}. Anything else in braces is left alone rather
        than breaking a generation.
      </p>

      {FIELDS.map(({ key, label, hint, rows }) => (
        <label key={key} className="block text-xs text-gray-500 mb-4">
          {label}
          {rows === 1 ? (
            <input value={form[key] ?? ''} className={field} placeholder={defaults[key] || ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
          ) : (
            <textarea value={form[key] ?? ''} rows={rows} className={field}
              placeholder={defaults[key] || ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
          )}
          {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
        </label>
      ))}

      <button onClick={() => save.mutate()} disabled={save.isPending}
        className="text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
        {save.isPending ? 'Saving…' : 'Save A/V script text'}
      </button>
    </div>
  );
}
